import { useState, useRef, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Bot, Send, Loader2, Trash2, User } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

export default function AdminAIPage() {
    const { admin, apiCall } = useAdminAuth();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionId] = useState(() => `admin-${admin?.id || 'default'}-${Date.now()}`);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Wrap in an explicit body so the effect returns undefined (not the result of scrollIntoView).
    // Prevents "destroy is not a function" in React StrictMode double-invoke.
    useEffect(() => { scrollToBottom(); }, [messages]);

    const sendMessage = async () => {
        if (!input.trim() || loading) return;
        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setLoading(true);
        try {
            const res = await apiCall('post', '/admin/ai/chat', { message: userMsg, session_id: sessionId });
            setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
        } catch (e) {
            const detail = e.response?.data?.detail || 'Error de AI';
            toast.error(detail);
            setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${detail}` }]);
        } finally {
            setLoading(false);
        }
    };

    const clearChat = async () => {
        try {
            await apiCall('delete', `/admin/ai/history?session_id=${sessionId}`);
            setMessages([]);
            toast.success('Chat limpiado');
        } catch { toast.error('Error'); }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-100px)]" data-testid="admin-ai-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        <Bot className="w-6 h-6 text-amber-500" />Agente de Soporte IA
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Experto en la plataforma AeroOps - GPT-5.2</p>
                </div>
                <Button variant="outline" size="sm" onClick={clearChat} data-testid="clear-chat-btn">
                    <Trash2 className="w-4 h-4 mr-1" />Limpiar Chat
                </Button>
            </div>

            {/* Messages */}
            <Card className="flex-1 overflow-hidden">
                <CardContent className="p-0 h-full flex flex-col">
                    <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ai-messages">
                        {messages.length === 0 && (
                            <div className="text-center py-16 text-muted-foreground">
                                <Bot className="w-16 h-16 mx-auto mb-4 opacity-20" />
                                <p className="text-lg font-medium">Agente de Soporte AeroOps</p>
                                <p className="text-sm mt-2 max-w-md mx-auto">
                                    Soy un experto en la plataforma AeroOps. Puedo ayudarte con diagnostico de errores,
                                    consultas sobre la arquitectura, y soporte tecnico.
                                </p>
                                <div className="mt-6 flex flex-wrap justify-center gap-2">
                                    {[
                                        'Un cliente reporta que no puede crear vuelos',
                                        'Como funciona el multi-tenancy?',
                                        'Que hago si un tenant excede su plan?',
                                        'Error 500 en el endpoint de crew'
                                    ].map((q, i) => (
                                        <button
                                            key={i}
                                            onClick={() => { setInput(q); }}
                                            className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
                                            data-testid={`suggestion-${i}`}
                                        >
                                            {q}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {messages.map((msg, i) => (
                            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                                {msg.role === 'assistant' && (
                                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                                        <Bot className="w-4 h-4 text-amber-500" />
                                    </div>
                                )}
                                <div className={`max-w-[75%] rounded-xl px-4 py-3 text-sm ${
                                    msg.role === 'user'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted/50'
                                }`}>
                                    {msg.role === 'assistant' ? (
                                        <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_pre]:bg-background [&_pre]:p-3 [&_pre]:rounded-lg [&_code]:text-xs">
                                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                                        </div>
                                    ) : (
                                        <p>{msg.content}</p>
                                    )}
                                </div>
                                {msg.role === 'user' && (
                                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <User className="w-4 h-4 text-primary" />
                                    </div>
                                )}
                            </div>
                        ))}
                        {loading && (
                            <div className="flex gap-3">
                                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                                    <Bot className="w-4 h-4 text-amber-500" />
                                </div>
                                <div className="bg-muted/50 rounded-xl px-4 py-3">
                                    <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="border-t p-4">
                        <div className="flex gap-2">
                            <Textarea
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Describe el problema o pregunta..."
                                rows={2}
                                className="resize-none"
                                disabled={loading}
                                data-testid="ai-input"
                            />
                            <Button onClick={sendMessage} disabled={loading || !input.trim()} className="self-end" data-testid="ai-send-btn">
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
